import type { FormComponentRef } from "@inertiajs/core"
import { Form } from "@inertiajs/react"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import HeadingSmall from "@/components/heading-small"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner"
import { SUPPORTED_LOCALES, nativeLabelFor } from "@/lib/i18n/locales"
import { settingsLocalePath } from "@/routes"
import type { Locale } from "@/types"

/**
 * OKS-96 — Language switcher section, mounted on
 * `pages/settings/appearance.tsx` below the existing theme toggle.
 *
 * Auto-submits on `Select.onValueChange`: a hidden `<input
 * name="locale">` bridges the Radix Select's component state into the
 * Inertia `<Form>` so the PATCH ships a flat `locale=...` param. The
 * server returns a 303 redirect to `/settings/appearance`; Inertia
 * follows with a fresh visit (`preserveState: false`) and the OKS-8
 * entrypoint's `syncI18nLocale` flips i18next on the next render — the
 * locale-flipped chrome itself is the success confirmation (no toast).
 *
 * Auto-submit uses the Inertia `<Form>` ref's `.submit()` method —
 * NOT `HTMLFormElement.requestSubmit()`, because Inertia owns the
 * form lifecycle and `submit()` plays cleanly with the visit + ref
 * state contract (errors, processing, etc.).
 *
 * Native labels ("English", "Français") render from
 * `SUPPORTED_LOCALES` and never translate. Brand-isolation neutral —
 * the controller writes only the user's own row.
 */
export default function LanguageSection({
  currentLocale,
}: {
  currentLocale: Locale
}) {
  const { t } = useTranslation()
  const [selectedLocale, setSelectedLocale] = useState<Locale>(currentLocale)
  const [previousLocale, setPreviousLocale] = useState<Locale>(currentLocale)
  const formRef = useRef<FormComponentRef | null>(null)

  // Re-sync if the prop flips under our feet (post-redirect, server returns
  // the new currentUser.locale; the page re-renders and we're already in
  // the new value — but a future "Cancel"-style redirect could revert).
  useEffect(() => {
    setSelectedLocale(currentLocale)
    setPreviousLocale(currentLocale)
  }, [currentLocale])

  return (
    <section
      data-testid="settings-locale-section"
      className="space-y-4"
      aria-labelledby="settings-locale-heading"
    >
      <div id="settings-locale-heading">
        <HeadingSmall
          title={t("settings.appearance.locale.title")}
          description={t("settings.appearance.locale.description")}
        />
      </div>

      <Form
        method="patch"
        action={settingsLocalePath()}
        ref={formRef}
        data-testid="settings-locale-form"
        options={{
          preserveScroll: false,
          preserveState: false,
        }}
        onError={() => {
          // Revert the optimistic Select value and surface a toast in
          // the user's CURRENT (pre-attempt) locale — the UI hasn't
          // reloaded yet, so `t(...)` resolves against the old bundle.
          setSelectedLocale(previousLocale)
          toast.error(t("settings.appearance.locale.error"), {
            action: {
              label: t("settings.appearance.locale.retry"),
              onClick: () => formRef.current?.submit(),
            },
          })
        }}
        className="space-y-3"
      >
        {({ processing }) => (
          <>
            <Label htmlFor="settings-locale-select">
              {t("settings.appearance.locale.label")}
            </Label>

            <div className="flex items-center gap-2">
              <Select
                value={selectedLocale}
                disabled={processing}
                onValueChange={(value) => {
                  // No-op when the same value is re-picked — avoids a
                  // round-trip + "preserveState: false" reload that
                  // would otherwise flicker the page for nothing.
                  if (value == null || value === selectedLocale) return
                  setPreviousLocale(selectedLocale)
                  setSelectedLocale(value)
                  // Submit on next tick so the hidden input has the
                  // updated value when Inertia serialises the form.
                  queueMicrotask(() => formRef.current?.submit())
                }}
              >
                <SelectTrigger
                  id="settings-locale-select"
                  data-testid="settings-locale-select"
                  aria-busy={processing}
                  aria-live="polite"
                  className="w-full sm:w-72"
                >
                  <SelectValue>{nativeLabelFor(selectedLocale)}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {SUPPORTED_LOCALES.map((entry) => (
                    <SelectItem
                      key={entry.value}
                      value={entry.value}
                      data-testid={`settings-locale-option-${entry.value}`}
                    >
                      {entry.nativeLabel}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {processing ? (
                <span
                  className="text-muted-foreground inline-flex items-center gap-1.5 text-sm"
                  data-testid="settings-locale-saving"
                >
                  <Spinner className="size-4" />
                  {t("settings.appearance.locale.saving")}
                </span>
              ) : null}
            </div>

            {/* Hidden bridge: Radix Select keeps its value in component
                state, not in a native <input>. This <input
                type="hidden" name="locale"> ensures the Inertia <Form>
                serialiser ships a flat `locale=...` param matching the
                server's strong-params shape (`params.permit(:locale)`).
                NEVER nest as `user[locale]` — wire-format canary
                in the Vitest spec asserts literal `name="locale"`. */}
            <input
              type="hidden"
              name="locale"
              value={selectedLocale}
              data-testid="settings-locale-hidden-input"
            />
          </>
        )}
      </Form>
    </section>
  )
}
